import { Injectable } from '@angular/core';
import { IframeCoordinatorClientService } from './iframe-coordinator-client.service';

/** Service responsible for sending notifications and toasts to the IFC host */
@Injectable()
export class NotificationService {
  private nextNotificationId = 0;
  private readonly baseId = 'admin-v3-notification-';
  constructor(private readonly ifcClient: IframeCoordinatorClientService) {}

  public error(message: string, options: IFCNotificationOptions = {}): IFCNotification {
    return this.requestNotification(message, { ...options, showCloseButton: true, type: 'error' });
  }

  public info(message: string, options: IFCNotificationOptions = {}): IFCNotification {
    return this.requestNotification(message, { ...options, type: 'info' });
  }

  public success(message: string, options: IFCNotificationOptions = {}): IFCNotification {
    return this.requestNotification(message, { ...options, type: 'success' });
  }

  public requestNotification(message: string, options: IFCNotificationOptions = {}): IFCNotification {
    const defaultOptions: IFCNotificationOptions = { hideAfter: 5, type: 'info' };
    const { title, ...custom } = {
      ...defaultOptions,
      ...options,
      id: options.id ? options.id : `${this.baseId}${++this.nextNotificationId}`
    };

    const notification = {
      title,
      message,
      custom
    };

    this.ifcClient.client.requestNotification(notification);

    return notification;
  }

  public dismissNotification(id: string): void {
    this.ifcClient.client.publish({ topic: 'dismissToast', payload: { id } });
  }
}

interface IFCNotification {
  title?: string;
  message: string;
  custom: Omit<IFCNotificationOptions, 'title'>;
}

interface IFCNotificationOptions {
  /** Id which can be referenced to dismiss the notification. If omitted, will be generated by an incremented value */
  id?: string;
  title?: string;
  type?: 'error' | 'info' | 'success';
  /** Number of seconds before the notification should be hidden. Will be disregarded for error notifications. */
  hideAfter?: number;
  /** Whether the close/dismiss button should be displayed. This must be true for error notifications. */
  showCloseButton?: boolean;
}
